import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_ORDER,
  ORDER_DIRECTIONS,
  OrderBy,
  type BaseFilterBy,
} from '@customTypes/cloudmix/api'
import { omitDefaults, pruneFilterBy } from '@lib/listStateNormalizer'

const DEFAULT_SORT: OrderBy[] = [DEFAULT_ORDER]
const EMPTY_FILTER = Object.freeze({})

export interface ListState<F extends Record<string, unknown> = BaseFilterBy> {
  pageIndex: number
  pageSize: number
  orderBy: OrderBy[]
  filterBy?: F
}

export interface ListSearchFields {
  pageIndex?: unknown
  pageSize?: unknown
  orderBy?: unknown
  filterBy?: unknown
}

export interface ListStateDefaults<F extends Record<string, unknown>> {
  pageIndex: number
  pageSize: number
  orderBy: OrderBy[]
  filterBy: F
}

export function parseSearchNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '') {
      return undefined
    }

    const parsed = Number(trimmed)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }

  return undefined
}

export function normalizeOrderBy(
  value: unknown,
  allowedOrderByFieldSet: Set<string> | null
): OrderBy[] | undefined {
  if (!Array.isArray(value) || value.length === 0) {
    return undefined
  }

  const normalized = value.filter((entry): entry is OrderBy => {
    if (!entry || typeof entry !== 'object') {
      return false
    }

    const candidate = entry as Partial<OrderBy>
    if (typeof candidate.field !== 'string') {
      return false
    }
    if (
      candidate.direction !== ORDER_DIRECTIONS.ascending &&
      candidate.direction !== ORDER_DIRECTIONS.descending
    ) {
      return false
    }

    if (
      allowedOrderByFieldSet &&
      !allowedOrderByFieldSet.has(candidate.field)
    ) {
      return false
    }

    return true
  })

  return normalized.length > 0 ? normalized : undefined
}

export function buildListDefaults<F extends Record<string, unknown>>({
  defaultSort,
  defaultFilterBy,
  allowedOrderByFieldSet,
}: {
  defaultSort?: OrderBy[]
  defaultFilterBy?: F
  allowedOrderByFieldSet: Set<string> | null
}): ListStateDefaults<F> {
  return {
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    orderBy:
      normalizeOrderBy(defaultSort, allowedOrderByFieldSet) ??
      normalizeOrderBy(DEFAULT_SORT, allowedOrderByFieldSet) ??
      [],
    filterBy: (pruneFilterBy(defaultFilterBy as F | undefined) ??
      EMPTY_FILTER) as F,
  }
}

export function hasListSearch(search: ListSearchFields): boolean {
  return (
    search.pageIndex !== undefined ||
    search.pageSize !== undefined ||
    search.orderBy !== undefined ||
    search.filterBy !== undefined
  )
}

export function toSparseListState<F extends Record<string, unknown>>(
  state: ListState<F>,
  defaults: ListStateDefaults<F>
): Partial<ListState<F>> {
  return omitDefaults(state, defaults)
}
